// Connected container — embeds `CaseDocumentPreview`, which fetches the raw
// document bytes (PDF iframe, image, CSV table, line-numbered text). Not
// storyable (the embedded preview needs network + QueryClient).
//
// The dark toolbar bar at the top is the overlay's own chrome — it sits above
// the embedded preview component.

import { Clapperboard } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CaseDocumentPreview } from '@/components/documents/CaseDocumentPreview'
import { TabularScopePreview } from '@/components/documents/TabularScopePreview'
import { DocumentFaceLayer } from '@/components/faces/DocumentFaceLayer'
import { pickFaceLayer } from '@/features/document-overlay/face-layer'
import { type DocumentFocus, NO_FOCUS } from '@/features/document-overlay/focus'
import type { UseDocumentFaceSectionResult } from '@/features/document-overlay/hooks/useDocumentFaces'
import { useDocumentFaces } from '@/features/document-overlay/hooks/useDocumentFaces'
import type { UseDocumentVideoResult } from '@/features/document-overlay/hooks/useDocumentVideo'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import type { CaseDocumentDetail } from '@/services/api/cases'
import { isVideoFileType } from '@/utils/video-file'
import { OverlayLoading } from './OverlayLoading'
import { RecordingTime } from './RecordingTime'
import { VideoWithTracks } from './VideoWithTracks'

export function DocumentPreview({
  caseId,
  file,
  focus = NO_FOCUS,
  faces,
  video,
}: {
  caseId: string
  file: CaseDocumentDetail
  /**
   * The face the overlay opened on and its page; an empty focus keeps the preview
   * exactly as it was.
   */
  focus?: DocumentFocus
  /**
   * The overlay's shared faces state. Omitted, the preview reads the faces on its
   * own and draws a read-only layer — that is how the identity *Photos* tab and
   * the stories use it.
   */
  faces?: UseDocumentFaceSectionResult
  /**
   * The recording's player state (PRD-0074): with it, a video previews as the player with
   * every tracked person boxed; without it, a video shows the file's plain preview.
   */
  video?: UseDocumentVideoResult
}) {
  const { t } = useTranslation()
  // A document with faces previews as the image the boxes belong to, so the
  // analyst sees *which* face put it here, and picking a face of another PDF page
  // switches the preview to that page. Everything else — no faces, a PDF opened
  // without a focus, a table — keeps its normal preview.
  const own = useDocumentFaces(caseId, faces ? null : file.id)
  const layer = pickFaceLayer(faces ? faces.section.allFaces : own.data?.faces, {
    focusFaceId: focus.faceId,
    focusPage: focus.page,
    selectedFaceId: faces?.selectedFaceId ?? null,
    fileType: file.file_type,
  })
  const layerImage = useAuthedImageUrl(layer?.sourceUrl ?? null)
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--background)',
      }}
    >
      <div
        className="flex group-data-[layout=stacked]/overlay:hidden"
        style={{
          height: 44,
          flexShrink: 0,
          alignItems: 'center',
          gap: 10,
          padding: '0 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--card)',
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'var(--muted-foreground)',
            fontWeight: 600,
          }}
        >
          {t('documents.tab.preview')}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex' }}>
        {video && isVideoFileType(file.file_type) ? (
          <VideoPane video={video} />
        ) : layer ? (
          <DocumentFaceLayer
            imageUrl={layerImage}
            alt={file.filename}
            boxes={layer.faces.map(face => ({
              faceId: face.face_id,
              box: face.box,
              state: face.state,
              label: face.display_name
                ? t('documents.detail.faceBoxOf', { name: face.display_name })
                : t('documents.detail.faceBox'),
            }))}
            focusedFaceId={faces?.selectedFaceId ?? layer.focusedFaceId}
            onSelect={faces?.select}
            className="h-full min-h-72 w-full border-0"
          />
        ) : file.import_scope?.scope_type === 'tabular' && file.import_scope.lake_document_id ? (
          // Tabular imports clip the lake document's Parquet (the table the
          // selection was built against), not the raw file bytes — preview the
          // same Parquet so it matches what extraction will see.
          <TabularScopePreview
            lakeDocumentId={file.import_scope.lake_document_id}
            selectedColumns={file.import_scope.selected_columns ?? null}
            selectedRows={file.import_scope.selected_rows ?? null}
          />
        ) : (
          <CaseDocumentPreview
            caseId={caseId}
            documentId={file.id}
            fileType={(file.file_type ?? '').toLowerCase()}
            filename={file.filename}
            selectedPages={
              file.import_scope?.scope_type === 'pages'
                ? (file.import_scope?.selected_pages ?? null)
                : null
            }
          />
        )}
      </div>
    </div>
  )
}

/**
 * The recording's pane: the player with the tracked people, or — before an analysis, or
 * for a run without a copy — a sentence saying what to do (the preview of the original file is
 * no help: it may be a codec no browser plays, and seeking needs the copy).
 */
function VideoPane({ video }: { video: UseDocumentVideoResult }) {
  const { t } = useTranslation()
  if (video.isLoading) return <OverlayLoading />
  const notice = video.notAnalysed
    ? {
        title: t('documents.detail.video.notAnalysed'),
        hint: t('documents.detail.video.notAnalysedHint'),
      }
    : video.isError
      ? { title: t('documents.detail.video.loadFailed'), hint: null }
      : video.playback && !video.playback.playback_url
        ? { title: t('documents.detail.video.noCopy'), hint: null }
        : null
  if (notice || !video.playback) {
    return (
      <div
        className="m-4 flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-border p-6 text-center text-sm text-muted-foreground"
        data-testid="video-notice"
      >
        <Clapperboard className="size-8 opacity-40" aria-hidden />
        <p className="text-foreground">{notice?.title ?? t('documents.detail.video.loadFailed')}</p>
        {notice?.hint ? <p className="text-xs">{notice.hint}</p> : null}
      </div>
    )
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
      {/* When the recording starts, and the one control that sets it (slice 06). */}
      <RecordingTime recording={video.recordedAt.recording} edit={video.recordedAt.edit} />
      <VideoWithTracks
        src={video.playback.playback_url}
        width={video.playback.width}
        height={video.playback.height}
        duration={video.duration}
        tracks={video.tracks}
        plates={video.plateTracks}
        objects={video.objectTracks}
        selectedTrackId={video.selectedTrackId}
        onSelectTrack={video.selectTrack}
        playRequest={video.playRequest}
        recordedAt={video.recordedAt.recording.time}
        onTime={video.notePlayhead}
        subtitles={video.subtitles}
        className="min-h-0 flex-1"
      />
    </div>
  )
}
